import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from, switchMap, tap } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl;
  private readonly apiScopes = ['api://c44bf44b-17ab-436b-ab84-82b4425c90b3/api.read'];

  constructor(
    private http: HttpClient,
    private msalService: MsalService
  ) {}

  // GET request with token
  get<T>(endpoint: string, params?: HttpParams): Observable<T> {
    console.log(`Making GET request to: ${this.apiUrl}/${endpoint}`);
    return this.getAccessToken().pipe(
      switchMap(token => {
        const headers = this.getHeaders(token);
        console.log('Request headers with token');
        return this.http.get<T>(`${this.apiUrl}/${endpoint}`, { params, headers }).pipe(
          tap({
            next: (response) => console.log('HTTP GET Response received:', response),
            error: (error) => console.error('HTTP GET Error:', error),
            complete: () => console.log('HTTP GET Complete')
          })
        );
      })
    );
  }

  // GET request with token, expecting a raw text/string response body (not JSON)
  getText(endpoint: string, params?: HttpParams): Observable<string> {
    return this.getAccessToken().pipe(
      switchMap(token => {
        const headers = this.getHeaders(token);
        return this.http.get(`${this.apiUrl}/${endpoint}`, { params, headers, responseType: 'text' });
      })
    );
  }

  // POST request with token
  post<T>(endpoint: string, data: any): Observable<T> {
    return this.getAccessToken().pipe(
      switchMap(token => {
        const headers = this.getHeaders(token);
        return this.http.post<T>(`${this.apiUrl}/${endpoint}`, data, { headers });
      })
    );
  }

  // PUT request with token
  put<T>(endpoint: string, data: any): Observable<T> {
    return this.getAccessToken().pipe(
      switchMap(token => {
        const headers = this.getHeaders(token);
        return this.http.put<T>(`${this.apiUrl}/${endpoint}`, data, { headers });
      })
    );
  }

  // DELETE request with token
  delete<T>(endpoint: string): Observable<T> {
    return this.getAccessToken().pipe(
      switchMap(token => {
        const headers = this.getHeaders(token);
        return this.http.delete<T>(`${this.apiUrl}/${endpoint}`, { headers });
      })
    );
  }

  // PATCH request with token
  patch<T>(endpoint: string, data: any): Observable<T> {
    return this.getAccessToken().pipe(
      switchMap(token => {
        const headers = this.getHeaders(token);
        return this.http.patch<T>(`${this.apiUrl}/${endpoint}`, data, { headers });
      })
    );
  }

  // Acquire access token for API
  private getAccessToken(): Observable<string> {
    return from(
      this.msalService.instance.acquireTokenSilent({
        scopes: this.apiScopes,
        account: this.msalService.instance.getAllAccounts()[0]
      }).then(result => {
        console.log('Access token acquired:', result.accessToken ? 'Token present' : 'No token');
        return result.accessToken;
      }).catch(error => {
        console.error('Error acquiring token silently, trying popup:', error);
        // If silent acquisition fails, try popup
        return this.msalService.instance.acquireTokenPopup({
          scopes: this.apiScopes
        }).then(result => result.accessToken);
      })
    );
  }

  // GET request returning a Blob (e.g. for PDF downloads)
  getBlob(endpoint: string): Observable<Blob> {
    return this.getAccessToken().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
        return this.http.get(`${this.apiUrl}/${endpoint}`, { headers, responseType: 'blob' });
      })
    );
  }

  // Helper method to get headers with token
  private getHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  // Get the current API URL (useful for debugging)
  getApiUrl(): string {
    return this.apiUrl;
  }
}
